// SmartStockAlert.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import './SmartStockAlert.css';

const SmartStockAlert = () => {
  const [stockData, setStockData] = useState([]);
  const [fulfilledPickups, setFulfilledPickups] = useState([]);
  const [awaitingOrders, setAwaitingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [brandFilter, setBrandFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');

  // 🔔 Audio for alerts
  const audioRef = useRef(null);

  // 

  // 🌐 Fetch stock directly from Google Sheets (FREE)
  const fetchStock = async () => {
    const SHEET_ID = '1t7ZqYS90wtiYn7R32PyKjnWo0cdsGxAndO239RgPNLc';
    const API_KEY = 'AIzaSyAftrTpF9YkPw2L7sTPH_tEbH2MfR0z82k';
    const RANGE = 'Sheet1!A:D'; // Columns: Product Name, Brand Name, Quantity, Entry Date

    const url = `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/${RANGE}?key=${API_KEY}`;

    try {
      const response = await fetch(url);
      const data = await response.json();

      if (!data.values || data.values.length === 0) {
        setStockData([]);
        return;
      }

      const rows = data.values;
      const headers = rows[0]; // First row = headers

      const mapped = rows.slice(1).map(row => {
        const item = {};
        row.forEach((cell, i) => {
          item[headers[i]] = cell;
        });

        // Parse Entry Date (DD/MM/YYYY)
        const entryDateStr = item['Entry Date'];
        let entryDate = null;
        if (entryDateStr) {
          const parts = entryDateStr.split('/');
          if (parts.length === 3) {
            entryDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
          } else {
            entryDate = new Date(entryDateStr);
          }
        }

        return {
          productName: item['Product Name']?.trim(),
          brandName: item['Brand Name']?.trim(),
          initialStock: parseInt(item['Quantity']) || 0,
          entryDate: entryDate
        };
      }).filter(item => item.productName && item.brandName && item.entryDate);

      setStockData(mapped);
    } catch (err) {
      console.error('Failed to load Google Sheet:', err);
      setError('Failed to load stock from Google Sheet');
    }
  };

  // 📦 Fetch fulfilled pickups
  const fetchPickups = async () => {
    try {
      const response = await fetch(
        'https://rest.livo.ma/pickups?_skip=0&_limit=1000000&status=fulfilled&_sort=-timestamps.updated',
        { credentials: 'include' }
      );
      const result = await response.json();
      setFulfilledPickups(result.data?.data || []);
    } catch (err) {
      console.error('Fetch pickups error:', err);
    }
  };

  // 📥 Fetch awaiting pickup orders
  const fetchAwaitingOrders = async () => {
    try {
      const response = await fetch(
        'https://rest.livo.ma/orders?_skip=0&_limit=10000&status=awaiting%20pickup&_sort=-timestamps.updated',
        { credentials: 'include' }
      );
      const result = await response.json();
      setAwaitingOrders(result.data?.data || []);
    } catch (err) {
      console.error('Fetch awaiting orders error:', err);
    }
  };

  // 🧮 Normalize strings for matching
  const normalize = (str) => str?.trim().toLowerCase() || '';

  // 🔁 Load data on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStock(), fetchPickups(), fetchAwaitingOrders()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // 🔁 Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('🔄 Auto-refreshing data...');
      const loadData = async () => {
        await Promise.all([fetchStock(), fetchPickups(), fetchAwaitingOrders()]);
      };
      loadData();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  // ✅ Compute alert cards
  const alertCards = useMemo(() => {
    if (!stockData.length || !fulfilledPickups.length || !awaitingOrders.length) return [];

    return stockData
      .map(item => {
        const key = `${normalize(item.productName)}|${normalize(item.brandName)}`;

        // ✅ Only pickups after entry date
        const totalPicked = fulfilledPickups
          .filter(pickup => {
            const pickupDate = new Date(pickup.timestamps.fulfilled || pickup.timestamps.updated);
            return pickupDate >= item.entryDate;
          })
          .reduce((sum, pickup) => {
            const brandName = pickup.client?.brand?.name;
            return sum + (pickup.products || [])
              .filter(p =>
                normalize(p.product?.name?.trim()) === normalize(item.productName) &&
                normalize(brandName) === normalize(item.brandName)
              )
              .reduce((pSum, p) => pSum + (p.quantity || 0), 0);
          }, 0);

        // ✅ Total awaiting
        const awaiting = awaitingOrders
          .filter(order => {
            const orderBrand = order.client?.brand?.name;
            return order.products?.some(p =>
              normalize(p.product?.name?.trim()) === normalize(item.productName) &&
              normalize(orderBrand) === normalize(item.brandName)
            );
          })
          .reduce((sum, order) => {
            return sum + order.products
              .filter(p =>
                normalize(p.product?.name?.trim()) === normalize(item.productName) &&
                normalize(order.client?.brand?.name) === normalize(item.brandName)
              )
              .reduce((pSum, p) => pSum + (p.quantity || 0), 0);
          }, 0);

        let bgColor = '#d1e7dd'; // 🟢 Light green (fully picked)
        let message = '🟢 Fully picked — no shortage';

        if (awaiting > 0) {
          if (totalPicked < item.initialStock) {
            bgColor = '#f8d7da'; // 🔴 Red
            message = `🔴 Incomplete: ${item.initialStock - totalPicked} left to pick up`;
          } else {
            bgColor = '#d1e7dd'; // 🟢 Green
            message = `🟢 Fully picked — ${awaiting} still awaiting pickup`;
          }
        }

        return {
          ...item,
          totalPicked,
          awaiting,
          bgColor,
          message
        };
      })
      // Add products in awaiting but NOT in stock sheet
      .concat(
        awaitingOrders
          .flatMap(order =>
            (order.products || [])
              .filter(p => {
                const productName = p.product?.name?.trim();
                const brandName = order.client?.brand?.name;
                if (!productName || !brandName) return false;
                const key = `${normalize(productName)}|${normalize(brandName)}`;
                return !stockData.some(item =>
                  `${normalize(item.productName)}|${normalize(item.brandName)}` === key
                );
              })
              .map(p => {
                const productName = p.product?.name?.trim();
                const brandName = order.client?.brand?.name;
                return {
                  productName,
                  brandName,
                  initialStock: 0,
                  entryDate: null,
                  totalPicked: 0,
                  awaiting: awaitingOrders
                    .filter(o => normalize(o.client?.brand?.name) === normalize(brandName))
                    .flatMap(o => o.products || [])
                    .filter(p2 => normalize(p2.product?.name?.trim()) === normalize(productName))
                    .reduce((sum, p2) => sum + (p2.quantity || 0), 0),
                  bgColor: '#d1d1d1', // ⚪ Gray
                  message: `❗ Not in stock list — ${p.quantity} awaiting`
                };
              })
          )
      )
      // Deduplicate
      .reduce((acc, item) => {
        const key = `${normalize(item.productName)}|${normalize(item.brandName)}`;
        const existing = acc.find(c => `${normalize(c.productName)}|${normalize(c.brandName)}` === key);
        if (existing) {
          existing.awaiting += item.awaiting;
        } else {
          acc.push({ ...item });
        }
        return acc;
      }, [])
      // Only show if awaiting > 0
      .filter(item => item.awaiting > 0)
      .filter(item => {
        if (brandFilter && !item.brandName.toLowerCase().includes(brandFilter.toLowerCase())) return false;
        if (productFilter && !item.productName.toLowerCase().includes(productFilter.toLowerCase())) return false;
        return true;
      });
  }, [stockData, fulfilledPickups, awaitingOrders, brandFilter, productFilter]);

  // 🔔 Sound alert if red card exists
  useEffect(() => {
    if (!alertCards.length) return;

    const hasRedAlert = alertCards.some(card => card.bgColor === '#f8d7da');
    if (hasRedAlert && audioRef.current) {
      audioRef.current.play().catch(err => console.warn('Audio play failed:', err));
    }
  }, [alertCards]);

  if (loading) return <div className="loading">>Loading data...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="smart-stock-alert">
      <h2>🚨 Smart Stock Alert & Manque Produit</h2>

      {/* Hidden alert sound */}
<audio ref={audioRef} src="/alert.mp3" preload="auto"></audio>
      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label>🏢 Brand:</label>
          <select value={brandFilter} onChange={e => setBrandFilter(e.target.value)}>
            <option value="">All Brands</option>
            {[...new Set(stockData.map(s => s.brandName))].sort().map(brand => (
              <option key={brand} value={brand}>{brand}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>🛍️ Product:</label>
          <input
            type="text"
            placeholder="Filter by product..."
            value={productFilter}
            onChange={e => setProductFilter(e.target.value)}
          />
        </div>
      </div>

      {/* Alerts */}
      {alertCards.length === 0 ? (
        <div className="all-ok">✅ All products are fully picked</div>
      ) : (
        <div className="alerts-grid">
          {alertCards.map((card, i) => (
            <div
              key={i}
              className="alert-card"
              style={{ backgroundColor: card.bgColor }}
            >
              <div className="product">{card.productName}</div>
              <div className="brand">by <strong>{card.brandName}</strong></div>
              <div className="info">📦 Stock: {card.initialStock}</div>
              <div className="info">✅ Picked: {card.totalPicked}</div>
              <div className="info">⏳ Awaiting: {card.awaiting}</div>
              <div className="message">{card.message}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SmartStockAlert;